import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import { BackButton } from "../../../common";
import { tabsItems } from "../../helpers/renderTabsItems";
import { renderActionButtons } from "../../helpers/renderComponents.helpers";
import LoaderDetails from "./components/LoaderDetails";
import {
  TabsDetails,
  ApprovalModal,
  RejectionModal,
  StatusBadge,
} from "../../components";
import ActionButtons from "../common/ActionButtons/ActionButtons";
import DebtResults from "../../../common/DebtLoadCalculation/components/DebtResults";
import {
  useEditMode,
  useRequestData,
  useRequestStatus,
} from "./hooks";
import {setApproveError, updateRequest} from "../../../../redux/actions/autoLombardActions";
import BasicAlert from "../../../utilites/Alert/Alert";
import { waitingMessage } from "../../../../helpers/data-constants";

import "./requestDetails.scss";
import {buttons, requestStatus, RequestStatuses} from "../../constants/constants";

const RequestDetailsComponent = ({
  setTitlePage,
  defaultActiveKey = tabsItems[0].key,
  onChange,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [activeKey, setActiveKey] = useState(defaultActiveKey || tabsItems[0].key);
  const { approveError } = useSelector(({ autolombard }) => autolombard.currentRequest);
  const { requestDetails, loading, debtLoading, form, comments, handleCommentChange, setRequestDetails } = useRequestData(setTitlePage);
  const [existOneAppUser, setExistOneAppUser] = useState(null);

  const {
    isEditing,
    changes,
    startEditing,
    handleChange,
    saveChanges,
    cancelEditing,
  } = useEditMode(requestDetails?.car_request || {});

  const {
    status,
    isApprovalOpen,
    isRejectionOpen,
    isKBResponse,
    showCommentFields,
    handleStatusChange,
    setStatus,
    setApprovalOpen,
    setRejectionOpen,
    setIsKBResponse,
  } = useRequestStatus(requestDetails, comments, changes);

  const activeItem = tabsItems.find((item) => item.key === activeKey);
  const { component: RenderComponent, isDisplayStatusBadge, isDisplayEditButton } = activeItem || {};
  const showEditButtons = status === RequestStatuses.IN_PROGRESS && isDisplayEditButton;
  const isEditingMode = isEditing && !showCommentFields.isShowComments
  const isApproveDisabled = !requestDetails?.pdn_calculation;

  const handleTabClick = useCallback(
    (key) => {
      setActiveKey(key);
      if (onChange) onChange(key);
    },
    [onChange]
  );

  const handleUpdateRequest = () => {
    const updatedData = saveChanges();
    setRequestDetails((prev) => ({
      ...prev,
      car_request: updatedData,
    }));
    dispatch(
      updateRequest({
        requestId: requestDetails?.car_request.id,
        data: { car_request: changes },
      })
    );
  };

  const renderStatusBadge = useCallback(() => {
    const currentStatus = requestStatus.find((s) => s.key === requestDetails?.car_request?.status);

    if (currentStatus) {
      const { label, key } = currentStatus;
      return <StatusBadge children={label} status={key} size="md" />;
    }
  }, [status, requestDetails]);

   useEffect(() => {
    if (requestDetails?.car_request?.status) {
      setStatus(requestDetails.car_request.status);
    }
  }, [requestDetails, setStatus]);

  useEffect(() => {
    setExistOneAppUser(
      requestDetails?.client_info?.oneAppId ? null : "CREATE_CLIENT"
    );
  }, [requestDetails?.client_info?.oneAppId]);
 
  if (loading) {
    return (
      <>
        <BackButton history={history} className="content-back" />
        <div className="d-flex flex-row flex-wrap gap-20">
          <div className="d-flex flex-column details-table-container">
              <LoaderDetails />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <BackButton history={history} className="content-back" />
      <div className="d-flex flex-row flex-wrap gap-20">
        <div className="d-flex flex-column details-table-container">
          <TabsDetails
            items={tabsItems}
            activeKey={activeKey}
            onTabClick={handleTabClick}
          />
            <FormProvider {...form}>
              <form>
                <div className="request-details">
                  <div className="d-flex flex-space-between m-b-spacer-1">
                    {isDisplayStatusBadge && renderStatusBadge()}
                    {showEditButtons && <ActionButtons
                      isEditing={isEditingMode}
                      onEdit={startEditing}
                      onSave={handleUpdateRequest}
                      onCancel={cancelEditing}
                    />}
                  </div>
                  {RenderComponent && (
                    <RenderComponent
                      details={requestDetails}
                      status={status}
                      showCommentFields={showCommentFields.isShowComments}
                      comments={comments}
                      isEditing={isEditing}
                      changes={changes}
                      handleCommentChange={handleCommentChange}
                      handleUpdateDetails={handleChange}
                    />
                  )}
                </div>
              </form>
            </FormProvider>
        </div>
        <div className="buttons-table-container">
          <div className="buttons-container">
            {renderActionButtons({
              status,
              buttons,
              disabled: existOneAppUser,
              handleStatusChange,
              additionalDisabledConditions: {
                [RequestStatuses.APPROVED]: isApproveDisabled
              }
            })}
            {renderActionButtons({status: existOneAppUser, buttons, handleStatusChange})}
          </div>
          {requestDetails?.pdn_calculation && <DebtResults debtLoadCalculation={requestDetails.pdn_calculation} /* calculateError={debtCalculateError} *//>}
        </div>
      </div>
      {isApprovalOpen && (
        <ApprovalModal
          isOpen={isApprovalOpen}
          requestDetails={requestDetails}
          onClose={() => setApprovalOpen(false)}
          setRequestStatusValue={setStatus}
          estimatedValue={requestDetails?.car_request?.estimatedValue}
        />
      )}
      <RejectionModal
        isOpen={isRejectionOpen}
        requestDetails={requestDetails}
        onClose={() => setRejectionOpen(false)}
        setRequestStatusValue={setStatus}
      />
      <BasicAlert
        onClose={() => setIsKBResponse(null)}
        message={debtLoading ? waitingMessage : isKBResponse}
        isOpen={isKBResponse || debtLoading}
        isError={isKBResponse || !debtLoading}
      />
      <BasicAlert
          onClose={() => {
            dispatch(setApproveError(null));
          }}
          message={approveError}
          isOpen={!!approveError}
          isError={true}
      />
    </>
  );
};

export default RequestDetailsComponent;
